

















import { Component, Prop } from 'vue-property-decorator';
import 'devextreme/data/odata/store';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import DataSource from 'devextreme/data/data_source';
import {
    Table,
    TableOptions,
    getTableCaption,
    columnTemplates,
} from '@/core/components/table';
import { Page } from '@/core/components/controls';
import {
    FetchServiceContract,
    CONTRACTS,
    EventsServiceContract,
    Events
    } from '@/core/contracts';
import { lookupField } from '@/core/helpers';
import { StructuredResult, PlannedOperationInfo } from '@/dto';
import { PlannedOperationsPageArgs } from './types';
import PlannedOperationsDetailTable from './PlannedOperationsDetailTable.vue';
import { ContextsState } from '@/store';
import { formatDate } from '@/core/helpers/DateTime';
import { localize } from '@/core/filters';

@Component({
    components: {
        PlannedOperationsDetailTable,
        TableOptions,
    },
})
export default class PlannedOperationsDetailPage extends Page {
    @Prop({ default: () => ({}) }) public args!: PlannedOperationsPageArgs;

    public table: Table | null = null;
    public tableKey: string = 'planned-operations-detail-control';

    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    public setTable(table: Table): void {
        this.table = table;
    }

    public get columnTemplates() {
        return columnTemplates;
    }

    public get dataSource(): DataSource {
        return new DataSource({
            key: 'id' as keyof PlannedOperationInfo,
            load: this.load,
        });
    }

    public async mounted() {
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    private onProcessingFinished({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.table!.refresh();
        }
    }

    public get localize() {
        return localize;
    }

    public get tableName(): string {
        return getTableCaption(this.tableKey) + this.pageHeader;
    }

    private get currentOperation() {
        return this.args.operation;
    }

    private get pageHeader() {
        let result = '';

        if (this.currentOperation!.startTime && this.currentOperation!.startTime.length > 0 ||
            this.currentOperation!.type && this.currentOperation!.type.length > 0 ||
            this.resource && this.resource.length > 0) {
                const haveStartTime = (this.currentOperation!.startTime &&
                                       this.currentOperation!.startTime.length > 0) as boolean;
                const haveResource = (this.resource && this.resource.length > 0) as boolean;
                const haveOperationType = (this.currentOperation!.type &&
                                           this.currentOperation!.type.length > 0) as boolean;
                result += ':';
                result += haveStartTime ? ' ' + formatDate(this.currentOperation!.startTime!) : '';
                result += haveOperationType ?
                          haveStartTime ? ', ' + localize(this.currentOperation!.type!, 'EventType') :
                                          ' ' + localize(this.currentOperation!.type!, 'EventType') : '';
                result += haveResource ? ' (' + this.resources + ')' : '';
            }

        return result;
    }

    private get resource() {
        return this.currentOperation?.mainResource.name;
    }

    private get resources() {
        const linkedResources: string[] | undefined = this.currentOperation!.linkedResource?.name.split(',');
        if (this.resource && !linkedResources?.find(res => res === this.resource)) {
            linkedResources?.push(this.resource);
        }
        return linkedResources ? linkedResources?.join(', ') : this.resource;
    }

    private async load() {
        const operationId = this.currentOperation?.id;
        const currentContextId = ContextsState.currentContextId;
        const objects = this.args!.flow ? 'flow' : 'packs';
        const url = `/contexts/${currentContextId}/operations/${operationId}/${objects}`;
        const data = await this._fetchService!.get<
            StructuredResult<PlannedOperationInfo>
        >(url);
        if (!data) {
            return {
                data: [],
                totalCount: 0,
            };
        }
        this.transform(data);
        const items = data.items || [];

        return {
            data: items,
            totalCount: items.length,
        };
    }

    private transform(data: StructuredResult<PlannedOperationInfo>) {
        if (!(data && data.items)) {
            return;
        }
        for (let index = 0; index < data.items.length; index++) {
            const item = data.items[index];
            item.id = index;

            lookupField(item, 'product', data.products);
            lookupField(item, 'startSite', data.sites);
            lookupField(item, 'finishSite', data.sites);
            lookupField(item, 'source', data.strings);
            lookupField(item, 'destination', data.strings);

            item.productType = data.resourceTypes[item.product.typeId];
        }
    }
}
